// Maps every validated PDF_STYLES behavior from the DevExtreme-based engine onto autotable's
// hooks. Dividers can't be drawn in didDrawCell (per-cell, immediately after that cell is
// painted) — the NEXT row's background fill happens after didDrawCell for the previous row,
// and paints directly over a line sitting exactly on the shared row boundary, silently erasing
// it (confirmed empirically: a bold, high-contrast test line drawn in didDrawCell was
// completely invisible in the rendered PDF; moving the same draw
// call into didDrawPage — which fires once per page, after every cell on that page is already
// drawn — made it appear correctly). So cell rects are only ever RECORDED in didDrawCell; the
// actual line-drawing happens in didDrawPage, guaranteed to be the last thing painted on the
// page.
import { PDF_STYLES_V2 } from './pdfStylesV2';
import { getStatusColor } from './statusColors';

function isNegativeDisplayValue(raw) {
    return typeof raw === 'string' && raw.trim().charAt(0) === '-';
}

export function buildHooksV2({ rowMeta, fontSize, leafColumns, groupLevelFontSizesPt }) {
    // Divider/border segments accumulate across a page and are drawn once per page in
    // didDrawPage — cleared after each page draws so a later page doesn't redraw an earlier
    // page's lines at the wrong coordinates. Same reasoning for both: didDrawCell fires right
    // after ITS cell is painted, but subsequent cells' own fills can paint over anything drawn
    // that early, so drawing is deferred to didDrawPage, guaranteed to run last on the page.
    let pendingDividers = [];
    let pendingHeaderBorder = [];

    return {
        didParseCell(data) {
            if (data.section === 'head') {
                data.cell.styles.fillColor = PDF_STYLES_V2.headerBgColor;
                data.cell.styles.textColor = PDF_STYLES_V2.headerAccentColor;
                data.cell.styles.fontStyle = 'bold';
                data.cell.styles.fontSize = fontSize + PDF_STYLES_V2.headerGroupFontSizeBoost;
                return;
            }

            const meta = rowMeta[data.row.index];
            if (!meta) return;

            if (meta.rowType === 'data') {
                data.cell.styles.valign = 'top';
            }
            if (meta.rowType === 'groupHeader') {
                data.cell.styles.fillColor = PDF_STYLES_V2.groupBg;
                data.cell.styles.fontStyle = 'bold';
            }
            if (meta.rowType === 'groupFooter' || meta.rowType === 'grandTotal' || meta.rowType === 'groupFooter') {
                // Every total row ('Total' and 'Grand Total') reads at the same size as plain
                // data rows, just bold — no header-boosted size for any of them.
                data.cell.styles.fontStyle = 'bold';
                data.cell.styles.fontSize = data.cell.styles.fontSize + 1;

            }
            // groupLevelFontSizesPt mirrors gc-data-grid.vue's applyGroupLevelFontSize — an
            // opt-in per-nesting-level font size (gridOptions.options.groupLevelFontSizes) that
            // overrides the flat default above. Grand totals aren't leveled in the live grid
            // either (only 'group'/'groupFooter' rows get it there), so left out here too.
            if ((meta.rowType === 'groupHeader') && groupLevelFontSizesPt) {
                const levelFontSize = groupLevelFontSizesPt[meta.level];
                if (levelFontSize) {
                    data.cell.styles.fontSize = levelFontSize;
                }
            }
            if (isNegativeDisplayValue(data.cell.raw)) {
                data.cell.styles.textColor = PDF_STYLES_V2.negativeColor;
            }

            if (meta.rowType === 'data') {
                const col = leafColumns && leafColumns[data.column.index];
                if (col && col.cellTemplate === 'Status') {
                    const statusColor = getStatusColor(data.cell.raw);
                    if (statusColor) {
                        data.cell.styles.textColor = statusColor;
                    }
                }
            }
        },

        didDrawCell(data) {
            if (data.section === 'head') {
                const { cell } = data;
                pendingHeaderBorder.push({ x1: cell.x, x2: cell.x + cell.width, y: cell.y + cell.height });
                return;
            }

            if (data.section !== 'body') return;
            const meta = rowMeta[data.row.index];
            if (!meta) return;

            const { cell } = data;
            pendingDividers.push({ x1: cell.x, x2: cell.x + cell.width, y: cell.y + cell.height });
        },

        didDrawPage(data) {
            const { doc } = data;

            if (pendingHeaderBorder.length) {
                // setDrawColor (unlike a cell style's textColor/fillColor, which autotable itself
                // spreads via unifyColor+apply) takes plain (r, g, b) arguments directly — an
                // array must be spread, or it's passed as a single non-numeric arg and produces
                // an invalid color operator in the PDF content stream.
                doc.setDrawColor(...PDF_STYLES_V2.headerAccentColor);
                doc.setLineWidth(PDF_STYLES_V2.headerBorderWidth);
                doc.setLineCap('butt');
                doc.setLineDashPattern([], 0); // solid — distinct from the dotted data-row divider below
                pendingHeaderBorder.forEach(({ x1, x2, y }) => doc.line(x1, y, x2, y));
                pendingHeaderBorder = [];
            }

            if (pendingDividers.length) {
                doc.setDrawColor(PDF_STYLES_V2.detailRowDividerColor);
                doc.setLineWidth(PDF_STYLES_V2.detailRowDividerWidth);
                doc.setLineCap('round');
                doc.setLineDashPattern(PDF_STYLES_V2.detailRowDividerDash, 0);
                pendingDividers.forEach(({ x1, x2, y }) => doc.line(x1, y, x2, y));
                // Reset immediately — neither autotable nor jsPDF resets dash/cap state between
                // draws, so leaving either set would leak into anything drawn after this (the
                // next page's own cell borders, etc.).
                doc.setLineDashPattern([], 0);
                doc.setLineCap('butt');
                pendingDividers = [];
            }
        },
    };
}
